import {
	CalendarDays,
	FileDown,
	HeartPulse,
	Loader2,
	MessageSquareMore,
	QrCode,
	Users,
	X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { useAttendance } from '@/api/queries/dds-attendance-queries'
import {
	useDDSById,
	useExportDDSPdf,
	useUpdateDDS,
} from '@/api/queries/dds-queries'
import { usePeople } from '@/api/queries/people-queries'
import { QRCodeButton } from '@/components/commons/qr-code-button'
import {
	DdsEmotionAccessInfo,
	DdsMoodActionDialog,
	type DdsMoodActionTarget,
	DdsMoodIcon,
} from '@/components/dds-attendance'
import { FormItem } from '@/components/forms/form-item'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardFooter,
	InspectionCardHeader,
	InspectionPhotoField,
} from '@/components/inspections'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { CardContent } from '@/components/ui/card'
import { Field, FieldContent } from '@/components/ui/field'
import { ROUTES } from '@/constants/routes'
import { useDdsBadgeScan } from '@/hooks/use-dds-badge-scan'
import { useAuthStore, useProfileStore } from '@/store'
import { useDDSStore } from '@/store/dds-store'
import { type BadgeScanMode, DDS_MOODS, type DDSType } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import {
	canManageDdsAttendance,
	canRegisterDdsMoodAction,
	canReviewDdsEmotions,
} from '@/utils/dds-attendance'
import { downloadBlob } from '@/utils/download-blob'
import { formatDateToDisplay } from '@/utils/formatDate'
import { convertHeicIfNeeded } from '@/utils/image-compression'

const typeConfig: Record<DDSType, { label: string; className: string }> = {
	DDS: {
		label: 'DDS',
		className:
			'bg-kaefer-honey-yellow/15 text-kaefer-pumpkin-orange border-kaefer-honey-yellow/30',
	},
	PIT_STOP: {
		label: 'PIT STOP',
		className:
			'bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue border-kaefer-sapphire-blue/30',
	},
	DIG: {
		label: 'DIG',
		className:
			'bg-kaefer-grass-green/15 text-kaefer-grass-green border-kaefer-grass-green/30',
	},
	DMI: {
		label: 'DMI',
		className: 'bg-kaefer-violet/15 text-kaefer-violet border-kaefer-violet/30',
	},
	DDS_GERAL: {
		label: 'DDS GERAL',
		className:
			'bg-kaefer-raspberry-pink/15 text-kaefer-raspberry-pink border-kaefer-raspberry-pink/30',
	},
}

export const DDSDetailPage = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const colors = INSPECTION_COLORS.honeyYellow

	if (!id) {
		return (
			<div className="p-6 text-center text-sm text-muted-foreground">
				ID da DDS inválido.
			</div>
		)
	}

	const {
		data: dds,
		isLoading: isLoadingDDS,
		isError: isErrorDDS,
		error: errorDDS,
	} = useDDSById(id)
	const { data: personData, isLoading: isLoadingPeople } = usePeople(true)
	const { resolveScannedPerson } = useDdsBadgeScan(personData)
	const { ddsIdsAttendees, setDdsIdsAttendees, removeDdsIdAttendee } =
		useDDSStore()
	const { defaultCustomerId } = useProfileStore()
	const user = useAuthStore((s) => s.user)

	const [newPersonIds, setNewPersonIds] = useState<string[]>(ddsIdsAttendees)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [ddsWasExpired, setDdsWasExpired] = useState(false)
	const [newImage, setNewImage] = useState<File | undefined>(undefined)
	const [newImagePreview, setNewImagePreview] = useState<string | null>(null)
	const [moodActionTarget, setMoodActionTarget] =
		useState<DdsMoodActionTarget | null>(null)
	const updateMutation = useUpdateDDS()
	const exportMutation = useExportDDSPdf()
	const canReviewEmotions = canReviewDdsEmotions(user, dds)
	// o emociograma só é pedido a quem pode vê-lo (id vazio mantém a query desligada)
	const { data: attendance } = useAttendance(canReviewEmotions ? id : '', false)
	const attendanceById = useMemo(
		() => new Map((attendance?.attendees ?? []).map((a) => [a.id, a])),
		[attendance],
	)

	const config = dds ? typeConfig[dds.type] : typeConfig.DDS // default value
	const existingImageUrl = useMemo(() => {
		if (!dds?.imagePath) return null
		if (/^https?:\/\//i.test(dds.imagePath)) return dds.imagePath
		return dds.imagePath
	}, [dds?.imagePath])

	// O prazo é definido na criação do DDS e devolvido pelo backend em closesAt
	useEffect(() => {
		if (!dds?.closesAt) {
			setDdsWasExpired(false)
			return
		}
		const expireAt = new Date(dds.closesAt).getTime()
		const now = Date.now()

		if (now >= expireAt) {
			setDdsWasExpired(true)
			return
		}

		setDdsWasExpired(false)
		const timeout = window.setTimeout(
			() => setDdsWasExpired(true),
			expireAt - now,
		)

		return () => window.clearTimeout(timeout)
	}, [dds?.closesAt])

	const lockedCodes = useMemo(() => {
		if (!dds) return new Set<string>()
		return new Set(
			dds.attendees
				.map((attendee) => attendee.personRegistration || attendee.employeeId)
				.filter(Boolean),
		)
	}, [dds])

	const handleCancel = () => {
		navigate(ROUTES.DDS.INDEX)
	}

	const handleDownloadPdf = () => {
		if (!dds) return
		exportMutation.mutate(dds.id, {
			onSuccess: (blob) =>
				downloadBlob(blob, `dds-${dds.date}-${dds.id.slice(0, 8)}.pdf`),
			onError: () => toast.error(t('dds.pdfError')),
		})
	}

	const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0]
		if (!picked) return

		const file = await convertHeicIfNeeded(picked)
		setNewImage(file)
		const preview = URL.createObjectURL(file)
		setNewImagePreview((current) => {
			if (current) URL.revokeObjectURL(current)
			return preview
		})
	}

	const handleRemovePhoto = () => {
		setNewImage(undefined)
		setNewImagePreview((current) => {
			if (current) URL.revokeObjectURL(current)
			return null
		})
	}

	useEffect(() => {
		return () => {
			if (newImagePreview) {
				URL.revokeObjectURL(newImagePreview)
			}
		}
	}, [newImagePreview])

	const resetNewPersonIds = () => {
		setNewPersonIds([])
	}

	const handleRemoveNewBadge = (idToRemove: string) => {
		setNewPersonIds((prev) =>
			prev.filter((personId) => personId !== idToRemove),
		)
		removeDdsIdAttendee(idToRemove)
	}

	const handleQRCodeScan = async (code: string, mode: BadgeScanMode) => {
		if (!dds) return

		const scannedPerson = await resolveScannedPerson(code, mode)
		if (!scannedPerson) return

		if (lockedCodes.has(scannedPerson.badge)) {
			toast.warning(t('dds.badgeAlreadyScanned'))
			return
		}

		let shouldAdd = false

		setNewPersonIds((prev) => {
			if (prev.includes(scannedPerson.id)) {
				return prev
			}

			shouldAdd = true
			return [...prev, scannedPerson.id]
		})

		setDdsIdsAttendees((prev) => {
			if (prev.includes(scannedPerson.id)) {
				return prev
			}

			shouldAdd = true
			return [...prev, scannedPerson.id]
		})

		if (shouldAdd) {
			toast.success(t('dds.badgeScanned', { name: scannedPerson.name }))
		} else {
			toast.warning(t('dds.badgeAlreadyScanned'))
		}
	}

	if (isLoadingDDS) {
		return (
			<div className="p-6 text-center text-sm text-muted-foreground">
				Carregando...
			</div>
		)
	}

	if (isErrorDDS) {
		console.error('Error fetching DDS', errorDDS)
		return (
			<div className="p-6 text-center text-sm text-destructive">
				{t('dds.loadError')}
			</div>
		)
	}

	if (!dds) {
		return (
			<div className="p-6 text-center text-sm text-muted-foreground">
				DDS não encontrado.
			</div>
		)
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (newPersonIds.length === 0 && !newImage) {
			toast.error(t('dds.noBadgesError'))
			return
		}

		if (!dds) {
			return
		}

		setIsSubmitting(true)
		try {
			const updateData: {
				customerId?: string
				attendeeIds?: string[]
				image?: File
			} = {}

			if (defaultCustomerId) {
				updateData.customerId = defaultCustomerId
			}

			if (newPersonIds.length > 0) {
				updateData.attendeeIds = newPersonIds
			}

			if (newImage) {
				updateData.image = newImage
			}

			await updateMutation.mutateAsync({
				id: dds.id,
				data: updateData,
			})
			toast.success(t('dds.saveSuccess'))
			navigate(ROUTES.DDS.INDEX)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<InspectionCard colors={colors}>
			<InspectionCardHeader
				actions={
					<Button
						className="w-auto gap-2"
						disabled={exportMutation.isPending}
						onClick={handleDownloadPdf}
						type="button"
						variant="outline"
					>
						{exportMutation.isPending ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<FileDown className="size-4" />
						)}
						{exportMutation.isPending
							? t('dds.downloadingPdf')
							: t('dds.downloadPdf')}
					</Button>
				}
				colors={colors}
				icon={<MessageSquareMore className="size-5" />}
				onBack={handleCancel}
				title={t('dds.title')}
			/>
			<DdsMoodActionDialog
				onClose={() => setMoodActionTarget(null)}
				target={moodActionTarget}
			/>
			<form onSubmit={handleSubmit} className="flex flex-1 flex-col">
				<CardContent className="flex flex-col gap-6 py-5">
					<div className="sm:grid-cols-2 gap-4 sm:gap-6">
						<CardContent className="px-4 py-4 rounded-xl border sm:px-5">
							{/* Top row: type badge + date */}
							<div className="flex items-center justify-between gap-2">
								{/* Subject */}
								<h3 className="font-semibold text-kaefer-dark-grey text-sm leading-snug line-clamp-2">
									{dds.subject}
								</h3>
								<span
									className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${config.className}`}
								>
									{config.label}
								</span>
							</div>

							<div className="flex items-center justify-between mt-2">
								<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
									<CalendarDays className="size-3.5" />
									{formatDateToDisplay(dds.date)}
								</span>

								<div className="flex items-center gap-1.5 text-xs text-kaefer-medium-grey">
									<Users className="size-3.5" />
									<span>
										{dds.attendeesCount + newPersonIds.length}{' '}
										{dds.attendeesCount + newPersonIds.length === 1
											? t('ddsList.participant')
											: t('ddsList.participants')}
									</span>
								</div>
							</div>

							{/* Description */}
							{dds.description && (
								<div className="mt-3 pt-3 border-t">
									<p className="text-xs text-muted-foreground leading-relaxed">
										{dds.description}
									</p>
								</div>
							)}
						</CardContent>
					</div>

					{/* Presença por QR: só quem criou (ou ADMIN) e enquanto o DDS estiver aberto */}
					{canManageDdsAttendance(user, dds) && !ddsWasExpired && (
						<div className="flex flex-col gap-3 rounded-xl border border-dashed border-kaefer-honey-yellow/60 bg-kaefer-honey-yellow/5 p-4 sm:flex-row sm:items-center sm:justify-between">
							<div className="flex min-w-0 flex-1 items-start gap-2">
								<QrCode className="mt-0.5 size-4 shrink-0 text-kaefer-pumpkin-orange" />
								<div className="min-w-0">
									<p className="font-medium text-kaefer-dark-grey text-sm">
										{t('ddsAttendance.title')}
									</p>
									<p className="text-muted-foreground text-xs">
										{t('ddsAttendance.openUntil', {
											time: new Date(dds.closesAt).toLocaleTimeString('pt-BR', {
												hour: '2-digit',
												minute: '2-digit',
											}),
										})}
									</p>
								</div>
							</div>
							<Button
								className="w-full sm:w-auto"
								onClick={() =>
									navigate(ROUTES.DDS.ATTENDANCE.replace(':id', dds.id))
								}
								type="button"
							>
								<QrCode className="size-4" />
								{t('ddsAttendance.viewOpen')}
							</Button>
						</div>
					)}

					{existingImageUrl ? (
						<FormItem label={t('dds.photo')} isRequired={false} error="">
							<InspectionPhotoField
								attachLabel={t('dds.attachPhoto')}
								altText={t('dds.photoSelected')}
								preview={existingImageUrl}
								onFileChange={() => {}}
								onRemove={() => {}}
								colors={colors}
								showRemoveButton={false}
								showPickerWhenEmpty={false}
								hideOnPreviewError
							/>
						</FormItem>
					) : ddsWasExpired ? (
						<div className="space-y-2">
							<p className="text-sm font-medium text-kaefer-dark-grey">
								{t('dds.photo')}
							</p>
							<Button
								type="button"
								variant="outline"
								disabled
								className="h-auto min-h-20 w-full"
							>
								{t('dds.attachPhoto')}
							</Button>
						</div>
					) : (
						<FormItem label={t('dds.photo')} isRequired={false} error="">
							<InspectionPhotoField
								attachLabel={t('dds.attachPhoto')}
								altText={t('dds.photoSelected')}
								preview={newImagePreview}
								onFileChange={handlePhotoChange}
								onRemove={handleRemovePhoto}
								colors={colors}
							/>
						</FormItem>
					)}

					<Field>
						<FieldContent>
							<QRCodeButton
								disabled={isLoadingPeople || ddsWasExpired}
								handler={handleQRCodeScan}
								resetScan={resetNewPersonIds}
								scannedCodes={newPersonIds}
								multiscan={true}
							/>
							{ddsWasExpired && (
								<Alert variant="destructive" className="text-center">
									<AlertTitle>{t('ddsList.ddsWasExpiredTitle')}</AlertTitle>
									<AlertDescription>
										{t('ddsList.ddsWasExpiredDescription')}
									</AlertDescription>
								</Alert>
							)}
							{newPersonIds.length > 0 && (
								<div className="mt-2 flex justify-end">
									<Button
										type="button"
										variant="cancel"
										size="sm"
										onClick={resetNewPersonIds}
									>
										{t('qrScanner.clearAll')}
									</Button>
								</div>
							)}
							{canReviewEmotions && attendance && (
								<div className="mt-3 flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2">
									<span className="flex items-center gap-1.5 text-xs font-semibold text-kaefer-medium-grey">
										<HeartPulse className="size-4" />
										{t('ddsEmotions.sectionTitle')}
										<DdsEmotionAccessInfo />
									</span>
									<div className="flex items-center gap-3">
										{DDS_MOODS.map((mood) => (
											<span
												className="flex items-center gap-1 text-sm font-semibold tabular-nums text-kaefer-dark-grey"
												key={mood}
											>
												<DdsMoodIcon className="size-5" mood={mood} />
												{attendance.moodSummary[mood]}
											</span>
										))}
									</div>
								</div>
							)}
							<div className="mt-3 flex flex-col gap-2 overflow-y-auto">
								{dds.attendees.map((attendee) => {
									const emotion = attendanceById.get(attendee.id)
									const mood = emotion?.mood ?? null

									return (
										<div
											key={attendee.id}
											className="flex flex-col gap-2 rounded-lg border bg-muted/20 px-3 py-2.5"
										>
											<div className="flex items-center gap-3">
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm font-medium text-kaefer-dark-grey">
														{attendee.personName}
													</p>
													<p className="text-xs text-muted-foreground">
														{t('dds.badgeCode')}:{' '}
														{attendee.personRegistration || attendee.employeeId}
													</p>
													{attendee.jobRole && (
														<p className="truncate text-xs text-muted-foreground">
															{t('dds.jobRole')}: {attendee.jobRole}
														</p>
													)}
												</div>
												{canReviewEmotions && mood && (
													<DdsMoodIcon className="size-6" mood={mood} />
												)}
												<span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
													{t(`ddsAttendance.source.${attendee.source}`)}
												</span>
											</div>
											{canReviewEmotions &&
												mood &&
												(canRegisterDdsMoodAction(mood) ||
													emotion?.moodAction) && (
													<div className="flex items-start justify-between gap-2 border-t pt-2">
														<p className="min-w-0 flex-1 whitespace-pre-wrap text-xs text-muted-foreground">
															{emotion?.moodAction ?? t('ddsEmotions.noAction')}
														</p>
														{canRegisterDdsMoodAction(mood) && (
															<Button
																className="w-auto shrink-0"
																onClick={() =>
																	setMoodActionTarget({
																		ddsId: dds.id,
																		attendeeId: attendee.id,
																		personName: attendee.personName,
																		mood,
																		moodAction: emotion?.moodAction ?? null,
																	})
																}
																size="xs"
																type="button"
																variant="outline"
															>
																{emotion?.moodAction
																	? t('ddsEmotions.editAction')
																	: t('ddsEmotions.registerAction')}
															</Button>
														)}
													</div>
												)}
										</div>
									)
								})}

								{newPersonIds.map((personId) => {
									const person = personData?.find((p) => p.id === personId)
									return (
										<div
											key={personId}
											className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5"
										>
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm font-medium text-kaefer-dark-grey">
													{person?.name || personId}
												</p>
												<p className="text-xs text-muted-foreground">
													{t('dds.badgeCode')}: {person?.badge || personId}
												</p>
											</div>
											<Button
												type="button"
												variant="ghost"
												size="icon-xs"
												onClick={() => handleRemoveNewBadge(personId)}
												aria-label={t('dds.removeBadge')}
												className="text-muted-foreground hover:text-destructive"
											>
												<X className="size-3.5" />
											</Button>
										</div>
									)
								})}
							</div>
						</FieldContent>
					</Field>
					{/* </div> */}
				</CardContent>
				<InspectionCardFooter
					colors={colors}
					isSubmitting={isSubmitting}
					wasExpired={ddsWasExpired}
					onCancel={handleCancel}
					cancelLabel={t('dds.cancel')}
					saveLabel={t('dds.save')}
					savingLabel={t('dds.saving')}
				/>
			</form>
		</InspectionCard>
	)
}
